import { Text } from "@blockdaemon-internal/precision-ui";
import { Trans } from "@lingui/react/macro";
import { Button, Card, Group, Stack } from "@mantine/core";
import { useEffect, useState } from "react";

// Global Error Listener - This component should be placed inside GlobalErrorBoundary
export const GlobalErrorListener = () => {
  const [pendingError, setPendingError] = useState<Error | null>(null);

  useEffect(() => {
    const handleGlobalError = (event: CustomEvent) => {
      const { error } = event.detail;
      // Use setTimeout to ensure the error is thrown in the next render cycle
      setTimeout(() => setPendingError(error), 0);
    };

    window.addEventListener("simulate-global-error", handleGlobalError as EventListener);

    return () => {
      window.removeEventListener("simulate-global-error", handleGlobalError as EventListener);
    };
  }, []);

  // Throw error during render to be caught by GlobalErrorBoundary
  if (pendingError) {
    // Reset the error state to prevent infinite error loop
    const errorToThrow = pendingError;
    setPendingError(null);
    throw errorToThrow;
  }

  return null; // This component doesn't render anything
};

// Widget Error Listener - This component should be placed inside WidgetErrorBoundary
export const WidgetErrorListener = () => {
  const [pendingError, setPendingError] = useState<Error | null>(null);

  useEffect(() => {
    const handleWidgetError = (event: CustomEvent) => {
      const { error } = event.detail;
      // Use setTimeout to ensure the error is thrown in the next render cycle
      setTimeout(() => setPendingError(error), 0);
    };

    window.addEventListener("simulate-widget-error", handleWidgetError as EventListener);

    return () => {
      window.removeEventListener("simulate-widget-error", handleWidgetError as EventListener);
    };
  }, []);

  // Throw error during render to be caught by WidgetErrorBoundary
  if (pendingError) {
    // Reset the error state to prevent infinite error loop
    const errorToThrow = pendingError;
    setPendingError(null);
    throw errorToThrow;
  }

  return null; // This component doesn't render anything
};

interface ErrorThrowingButtonProps {
  name: string;
  level: "global" | "widget";
  errorMessage?: string;
}

const ErrorThrowingButton = ({
  name,
  level,
  errorMessage = "Test error",
}: ErrorThrowingButtonProps) => {
  const handleThrowError = () => {
    const error = new Error(`[${level.toUpperCase()}] ${errorMessage} (from ${name})`);

    if (level === "widget") {
      // Dispatch event to be caught by WidgetErrorListener
      window.dispatchEvent(
        new CustomEvent("simulate-widget-error", {
          detail: { error, message: errorMessage, level },
        }),
      );
    } else {
      // Dispatch event to be caught by GlobalErrorListener
      window.dispatchEvent(
        new CustomEvent("simulate-global-error", {
          detail: { error, message: errorMessage, level },
        }),
      );
    }
  };

  return (
    <Button variant="destructive" onClick={handleThrowError}>
      <Trans>Throw {level} Error</Trans>
    </Button>
  );
};

export const ErrorTestingPlayground = () => {
  return (
    <Card>
      <Stack spacing="md">
        <Text fw={600} size="sm" c="gray.9">
          <Trans>🧪 Error Testing Components</Trans>
        </Text>
        <Text size="xs">
          <Trans>These components can throw errors to test different error boundaries</Trans>
        </Text>

        <Group spacing="sm">
          <ErrorThrowingButton
            name="global-error-test"
            level="global"
            errorMessage="Global boundary test error"
          />
          <ErrorThrowingButton
            name="widget-error-test"
            level="widget"
            errorMessage="Widget boundary test error"
          />
        </Group>
      </Stack>
    </Card>
  );
};
